'use client';

import { useMemo, useCallback } from 'react';
import { ClubSchedulesStep } from '@/features/tournament-wizard/sports/shared/steps/ClubSchedulesStep';
import { createEmptyDraft } from '@/features/tournament-wizard/store/wizardStore';
import type { TournamentDraft, ClubSchedulesSlice } from '@/features/tournament-wizard/engine/types';
import { CATEGORY_RANKING_TARGET } from '@/types/tournament.model';
import type { Tournament, TournamentFormData } from '@/types/tournament.model';

export interface ClubSchedulesStepEmbedProps {
  tournament: Tournament;
  formData: TournamentFormData;
  value: ClubSchedulesSlice;
  onChange: (next: ClubSchedulesSlice) => void;
  /** When set, freezes the club list to these ids. See {@link ClubSchedulesStep}. */
  allowedClubIds?: string[];
}

/**
 * Embeds the wizard {@link ClubSchedulesStep} outside the wizard engine (edit modal step 2).
 * Uses a minimal draft built from the current tournament + form data.
 */
export function ClubSchedulesStepEmbed({
  tournament,
  formData,
  value,
  onChange,
  allowedClubIds,
}: ClubSchedulesStepEmbedProps) {
  const draft = useMemo((): TournamentDraft => {
    const d = createEmptyDraft();
    d.sport = { sport: tournament.sport };
    d.tournamentConfig = {
      signupDeadlineDate: formData.signupDeadlineDate,
      matchDuration: formData.matchDuration,
      minSchedulesRequired: formData.minSchedulesRequired ?? null,
      countsForCategoryRanking: formData.countsForCategoryRanking ?? true,
      categoryRankingTarget: CATEGORY_RANKING_TARGET.PLAYER_CATEGORY,
      categoryRankingCategory: null,
      isValid: true,
    };
    d.fixtureConfig = {
      fixtureType: formData.fixtureType,
      format: formData.format,
      teamsPerGroups: formData.teamsPerGroups,
      classifiedPerGroups: formData.classifiedPerGroups,
      minTeamsAllowed: formData.minTeamsAllowed,
      maxTeamsAllowed: formData.maxTeamsAllowed,
    };
    return d;
  }, [
    tournament.sport,
    formData.signupDeadlineDate,
    formData.matchDuration,
    formData.minSchedulesRequired,
    formData.countsForCategoryRanking,
  ]);

  const updateData = useCallback(
    (patch: Partial<ClubSchedulesSlice>) => {
      onChange({ ...value, ...patch });
    },
    [value, onChange],
  );

  return (
    <div className="max-h-[65vh] overflow-y-auto pr-1">
      <ClubSchedulesStep
        data={value}
        updateData={updateData}
        draft={draft}
        allowedClubIds={allowedClubIds}
        isEditing
      />
    </div>
  );
}
